import { useMemo, useState } from "react";
import { Check, X, Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LangContext";
import { useSettings } from "@/context/SettingsContext";
import { AuthImage } from "@/components/AuthImage";
import { ReceiptPreview } from "@/components/ReceiptPreview";
import { PageLoader } from "@/components/ProtectedRoute";
import { useAdminUsers } from "@/hooks/useAdminUsers";

const Info = ({ label, value, testId, cls = "" }) => (
  <div><p className="text-xs text-muted-foreground">{label}</p><p className={`font-semibold ${cls}`} data-testid={testId}>{value}</p></div>
);

const ReviewCard = ({ u, pkg, onStatus, onZoom }) => {
  const { t, tr } = useLang();
  return (
    <div className="grid gap-5 rounded-2xl border border-border bg-card p-5 lg:grid-cols-12" data-testid={`payment-review-card-${u.id}`}>
      <div className="grid grid-cols-2 gap-4 lg:col-span-5">
        <Info label={t("full_name")} value={u.full_name} />
        <Info label={t("phone")} value={u.phone} cls="font-mono text-sm" />
        <Info label={t("country")} value={u.country} />
        <Info label={t("package")} value={pkg ? tr(pkg.name) : u.package_id} testId={`payment-review-package-${u.id}`} />
        <Info label={t("reg_payment")} value={pkg?.price} cls="text-primary" />
        <Info label={t("reward")} value={pkg?.reward} cls="text-amber-600 dark:text-amber-400" />
        <Info label={t("submitted_on")} value={new Date(u.payment_submitted_at).toLocaleString()} />
      </div>
      <div className="grid grid-cols-2 gap-3 lg:col-span-5">
        <button type="button" onClick={() => onZoom({ u, kind: "id" })} className="text-left" data-testid={`payment-review-id-${u.id}`}>
          <p className="mb-1 text-xs text-muted-foreground">{t("view_photo")}</p>
          <AuthImage path={u.id_photo_path} alt="ID" className="h-32 w-full rounded-xl border border-border object-cover" />
        </button>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">{t("receipt")}</p>
          {u.payment_receipt_type === "application/pdf"
            ? <ReceiptPreview path={u.payment_receipt_path} type={u.payment_receipt_type} testId={`payment-review-receipt-${u.id}`} />
            : <button type="button" onClick={() => onZoom({ u, kind: "receipt" })} className="w-full" data-testid={`payment-review-receipt-${u.id}`}><AuthImage path={u.payment_receipt_path} alt="receipt" className="h-32 w-full rounded-xl border border-border object-cover" /></button>}
        </div>
      </div>
      <div className="flex gap-2 lg:col-span-2 lg:flex-col lg:justify-center">
        <button onClick={() => onStatus(u.id, "approved")} data-testid={`payment-approve-button-${u.id}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700"><Check className="h-4 w-4" />{t("approve")}</button>
        <button onClick={() => onStatus(u.id, "rejected")} data-testid={`payment-reject-button-${u.id}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-rose-300 px-4 py-2.5 text-sm font-bold text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950"><X className="h-4 w-4" />{t("reject")}</button>
      </div>
    </div>
  );
};

export const PaymentReviews = () => {
  const { t } = useLang();
  const { settings } = useSettings();
  const { users, setStatus } = useAdminUsers(t);
  const [zoom, setZoom] = useState(null);
  const pending = useMemo(() => (users || []).filter((u) => u.payment_receipt_path && u.status === "pending"), [users]);

  if (!users) return <PageLoader />;
  const pkgOf = (id) => settings?.packages?.find((p) => p.id === id);

  return (
    <div className="space-y-5" data-testid="admin-payment-reviews">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">{t("payments_subtitle")}</p>
        <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-sm font-bold text-amber-900 dark:bg-amber-950 dark:text-amber-300" data-testid="admin-payments-count"><Wallet className="h-4 w-4" />{pending.length}</span>
      </div>
      {pending.map((u) => <ReviewCard key={u.id} u={u} pkg={pkgOf(u.package_id)} onStatus={setStatus} onZoom={setZoom} />)}
      {!pending.length && <p className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="admin-no-pending-payments">{t("no_pending_payments")}</p>}
      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-payment-zoom-dialog">
          <DialogTitle>{zoom?.kind === "receipt" ? t("receipt") : t("view_photo")} — {zoom?.u.full_name}</DialogTitle>
          {zoom && <AuthImage path={zoom.kind === "receipt" ? zoom.u.payment_receipt_path : zoom.u.id_photo_path} alt="" className="max-h-[70vh] w-full rounded-xl object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
